import {Injectable} from '@angular/core';
import {defer, Observable, of} from 'rxjs';
import {map, takeUntil, tap} from 'rxjs/operators';
import {createPendingRequest, PendingRequest} from '../../common/request-synchronization';
import {AccountType} from '../../enums/account-type';
import {Exchange} from '../../enums/exchange';
import {OrderTransactionType} from '../../enums/order-transaction-type';
import {OrdType} from '../../enums/order-type';
import {Side} from '../../enums/side';
import {TimeInForce} from '../../enums/time-in-force';
import {UserRole} from '../../enums/user-role';
import {ExchangeAccountBase} from '../../models/exchange-account';
import {NewOrder} from '../../models/order';
import {Security} from '../../models/security';
import {clearSecurityKey, formatSecurityKey, SecurityKey} from '../../models/security-key';
import {OrderService} from '../../services/orders';
import {getNewOrderParamsByAccountBase} from '../../widgets/components/order-placing/order-placing-utils';
import {AuthService} from '../auth/auth.service';
import {destroy$} from '../destroyable';
import {
    getConstantNotSelectableLeveragesForSides,
    getDefaultLeveragesForSides,
    getLeveragesBySide,
    getLeveragesFromSecurity,
    isLeverageSelectable,
    isLeverageSelectableForSecurity,
    isSideLeverageSelectable,
    LeveragesForSides
} from './leverage';

/**
 * TODO: Currently this API supports only OKEx. But in future it should be used for all others.
 */
const LEVERAGES_API_EXCHANGES: Exchange[] = [
    Exchange.OKEX
];

interface LeverageRequestCacheRec {
    result: LeveragesForSides;
    timestamp: number;
}

@Injectable()
export class LeverageService {

    public readonly destroy$ = destroy$();

    private pendingLeverageRequests: {[key: string]: PendingRequest<LeveragesForSides>} = {};

    private leverageRequestsCache: {[key: string]: LeverageRequestCacheRec} = {};

    constructor(
        private orderService: OrderService,
        private authService: AuthService
    ) {
        this.listenToAuthorization();
    }

    public isLeverageSelectableForSecurity(security: Security): boolean {
        return security
            ? LEVERAGES_API_EXCHANGES.includes(security.securityExchange)
                ? true
                : isLeverageSelectableForSecurity(security)
            : false;
    }

    public getLeveragesForSecurityAcc(
        security: Security,
        accountBase: ExchangeAccountBase,
        isSelectableForSecurity: boolean = this.isLeverageSelectableForSecurity(security)
    ): Observable<LeveragesForSides> {
        let result$: Observable<LeveragesForSides>;
        const isMargin = accountBase && accountBase.type === AccountType.MARGIN;

        if (isMargin) {
            if (isSelectableForSecurity) {
                result$ = this.innerGetLeverages(security, accountBase);
            } else {
                result$ = of(getConstantNotSelectableLeveragesForSides());
            }
        } else {
            result$ = of(getDefaultLeveragesForSides());
        }

        return result$;
    }

    public getSideLeveragesForSecurityAcc(
        security: Security,
        accountBase: ExchangeAccountBase,
        side: Side
    ): Observable<number[]> {
        return this.getLeveragesForSecurityAcc(security, accountBase).pipe(
            map(leveragesForSides => getLeveragesBySide(leveragesForSides, side))
        );
    }

    public isLeveragesSelectableForSecurityAcc(
        security: Security,
        accountBase: ExchangeAccountBase
    ): Observable<boolean> {
        return this.getLeveragesForSecurityAcc(
            security,
            accountBase
        ).pipe(
            map(leveragesForSides => isLeverageSelectable(leveragesForSides))
        );
    }

    public isSideLeverageSelectableForSecurityAcc(
        security: Security,
        accountBase: ExchangeAccountBase,
        side: Side
    ): Observable<boolean> {
        return this.getLeveragesForSecurityAcc(
            security,
            accountBase
        ).pipe(
            map(leveragesForSides =>
                isSideLeverageSelectable(getLeveragesBySide(leveragesForSides, side))
            )
        );
    }

    public correctSideLeverage(
        security: Security,
        accountBase: ExchangeAccountBase,
        side: Side,
        leverage: number | undefined
    ): Observable<number | undefined> {
        return this.getSideLeveragesForSecurityAcc(security, accountBase, side).pipe(
            map(leveragesAllowed =>
                leveragesAllowed.includes(leverage) ? leverage : leveragesAllowed[0]
            )
        );
    }

    public correctLeverageBySideMap(
        security: Security,
        accountBase: ExchangeAccountBase,
        leverageBySideMap: Partial<Record<Side, number>>
    ): Observable<Partial<Record<Side, number>>> {
        return this.getLeveragesForSecurityAcc(security, accountBase).pipe(
            map(leveragesForSides =>
                [Side.BUY, Side.SELL].reduce(
                    (acc, side) => {
                        const currentLeverage = leverageBySideMap && leverageBySideMap[side];
                        const allowedSideLeverages = getLeveragesBySide(leveragesForSides, side);

                        acc[side] = allowedSideLeverages.includes(currentLeverage)
                            ? currentLeverage
                            : allowedSideLeverages[0];

                        return acc;
                    },
                    {} as Partial<Record<Side, number>>
                )
            )
        );
    }

    private getFakeOrderForLeverageRequest(security: Security, accountBase: ExchangeAccountBase): NewOrder {
        return {
            security:   clearSecurityKey(security) as Security,
            securityId: null,
            ...getNewOrderParamsByAccountBase(accountBase),
            side:            Side.BUY,
            orderQty:        1,
            timeInForce:     TimeInForce.GTC,
            ordType:         OrdType.MARKET,
            transactionType: OrderTransactionType.NEW
        } as NewOrder;
    }

    private listenToAuthorization(): void {
        this.authService.userRole$.pipe(
            tap((userRole: UserRole) => {
                if (!userRole) {
                    this.pendingLeverageRequests = {};
                    this.leverageRequestsCache = {};
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private innerGetLeverages(security: Security, accountBase: ExchangeAccountBase): Observable<LeveragesForSides> {
        const doRequest = (): Observable<LeveragesForSides> => {
            const order = this.getFakeOrderForLeverageRequest(security, accountBase);

            return LEVERAGES_API_EXCHANGES.includes(security.securityExchange)
                ? this.orderService.requestLeverages(order)
                : of(getLeveragesFromSecurity(security));
        };

        const requestKey = this.getLeveragesRequestKey(security, accountBase);

        const doCachedRequest = (): Observable<LeveragesForSides> =>
            this.callCachedLeverageRequest(requestKey, doRequest);

        const pendingRequest = this.pendingLeverageRequests[requestKey]
            || createPendingRequest(
                defer(doCachedRequest),
                newPendingRequest =>
                    this.pendingLeverageRequests[requestKey] = newPendingRequest,
                () => delete this.pendingLeverageRequests[requestKey]
            );

        return pendingRequest.request$;
    }

    private getLeveragesRequestKey(securityKey: SecurityKey, accountBase: ExchangeAccountBase): string {
        return [
            formatSecurityKey(securityKey),
            accountBase && accountBase.id || '',
            accountBase && accountBase.type || ''
        ].join('_');
    }

    private callCachedLeverageRequest(
        requestKey: string,
        doRequest: () => Observable<LeveragesForSides>
    ): Observable<LeveragesForSides> {
        const CACHE_TIMEOUT_MS = 1000 * 10; // 10 sec

        let cachedResult$: Observable<LeveragesForSides>;

        if (this.leverageRequestsCache.hasOwnProperty(requestKey)) {
            const cacheRec = this.leverageRequestsCache[requestKey];

            if (cacheRec) {
                if (Date.now() - cacheRec.timestamp <= CACHE_TIMEOUT_MS) {
                    cachedResult$ = of(cacheRec.result);
                } else {
                    delete this.leverageRequestsCache[requestKey];
                }
            }
        }

        return cachedResult$ || doRequest().pipe(
            tap(result =>
                this.leverageRequestsCache[requestKey] = {
                    result,
                    timestamp: Date.now()
                }
            )
        );
    }

}
